import React from "react";
import { Box, Container, Heading, Text, SimpleGrid, VStack, HStack, Icon, Badge, Divider, Button } from "@chakra-ui/react";
import { FaSolarPanel, FaHome, FaBolt, FaCheckCircle, FaChartLine } from "react-icons/fa";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const NAVY = "#141C49";
const TEAL = "#0D7F6E";
const SAND = "#C2A878";
const OFF_WHITE = "#F0EBE0";

const PropertyServicesPropertySolarPackage: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/property/services/property-solar-package"
        fallbackTitle="Property + Solar Package – One Property Australia"
        fallbackDescription="Integrated property investment with solar solutions. Bundle property acquisition and solar installation for maximum value."
      />
      <NavbarWithCallToAction />

      <Box bg={NAVY} color="white" py={20}>
        <Container maxW="container.xl">
          <HStack spacing={12} justify="space-between">
            <VStack align="flex-start" spacing={6} flex={1}>
              <Badge bg={SAND} color={NAVY} px={4} py={2} borderRadius="full" fontSize="md">BUNDLE & SAVE</Badge>
              <Heading size="3xl" fontWeight="black">Property + Solar Package</Heading>
              <Text fontSize="xl" lineHeight="1.8">The ultimate value-add investment: property acquisition plus solar installation in one seamless package</Text>
            </VStack>
            <HStack spacing={-4} display={{ base: "none", lg: "flex" }}>
              <Icon as={FaHome} boxSize={20} opacity={0.7} />
              <Text fontSize="4xl" fontWeight="bold">+</Text>
              <Icon as={FaSolarPanel} boxSize={20} opacity={0.7} />
            </HStack>
          </HStack>
        </Container>
      </Box>

      <Container maxW="container.xl" py={16}>
        <VStack spacing={4} textAlign="center" mb={12}>
          <Heading size="xl">Package Benefits</Heading>
          <Text fontSize="lg" color="gray.600">Why bundle property and solar together?</Text>
        </VStack>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
          <Box bg={OFF_WHITE} p={8} borderRadius="2xl" border="2px solid" borderColor={SAND}>
            <Icon as={FaHome} boxSize={12} color={TEAL} mb={4} />
            <Heading size="md" mb={4}>Property Component</Heading>
            <VStack align="stretch" spacing={3}>
              <HStack><Icon as={FaCheckCircle} color={TEAL} /><Text fontSize="sm">Full property search & acquisition</Text></HStack>
              <HStack><Icon as={FaCheckCircle} color={TEAL} /><Text fontSize="sm">Market analysis & due diligence</Text></HStack>
              <HStack><Icon as={FaCheckCircle} color={TEAL} /><Text fontSize="sm">Negotiation & settlement support</Text></HStack>
              <HStack><Icon as={FaCheckCircle} color={TEAL} /><Text fontSize="sm">Finance coordination</Text></HStack>
            </VStack>
          </Box>

          <Box bg="gray.50" p={8} borderRadius="2xl" border="2px solid" borderColor="gray.200">
            <Icon as={FaSolarPanel} boxSize={12} color={SAND} mb={4} />
            <Heading size="md" mb={4}>Solar Component</Heading>
            <VStack align="stretch" spacing={3}>
              <HStack><Icon as={FaCheckCircle} color={SAND} /><Text fontSize="sm">6.6kW premium solar system</Text></HStack>
              <HStack><Icon as={FaCheckCircle} color={SAND} /><Text fontSize="sm">Tier 1 panels & inverter</Text></HStack>
              <HStack><Icon as={FaCheckCircle} color={SAND} /><Text fontSize="sm">Professional installation</Text></HStack>
              <HStack><Icon as={FaCheckCircle} color={SAND} /><Text fontSize="sm">10-year warranty</Text></HStack>
            </VStack>
          </Box>
        </SimpleGrid>
      </Container>

      <Box bg="gray.50" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center" mb={12}>
            <Heading size="xl">Cost Breakdown</Heading>
            <Text fontSize="lg" color="gray.600">Transparent pricing with no hidden fees</Text>
          </VStack>

          <Box bg="white" maxW="3xl" mx="auto" p={8} borderRadius="2xl" boxShadow="lg">
            <VStack spacing={4} align="stretch">
              <HStack justify="space-between" py={3}>
                <Text fontWeight="bold">Property Service Fee</Text>
                <Text fontWeight="bold">$5,995</Text>
              </HStack>
              <Divider />
              <HStack justify="space-between" py={3}>
                <Text fontWeight="bold">Solar Installation</Text>
                <Text fontWeight="bold">$8,500</Text>
              </HStack>
              <Divider />
              <HStack justify="space-between" py={3}>
                <Text color="gray.600">Subtotal</Text>
                <Text color="gray.600">$14,495</Text>
              </HStack>
              <HStack justify="space-between" py={3} bg={OFF_WHITE} px={4} borderRadius="lg">
                <Text fontWeight="bold" color={TEAL}>Package Discount</Text>
                <Text fontWeight="bold" color={TEAL}>-$2,500</Text>
              </HStack>
              <Divider borderWidth="2px" borderColor={TEAL} />
              <HStack justify="space-between" py={4}>
                <Heading size="lg" color={TEAL}>Total Package</Heading>
                <Heading size="lg" color={TEAL}>$11,995</Heading>
              </HStack>
              <Text fontSize="sm" color="gray.600" textAlign="center">Plus property purchase price & solar rebates apply</Text>
            </VStack>
          </Box>
        </Container>
      </Box>

      <Container maxW="container.xl" py={16}>
        <VStack spacing={4} textAlign="center" mb={12}>
          <Heading size="xl">Value Add to Your Investment</Heading>
        </VStack>
        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={6}>
          <Box textAlign="center">
            <Icon as={FaChartLine} boxSize={12} color={TEAL} mb={4} />
            <Heading size="md" mb={2}>+$25K</Heading>
            <Text fontSize="sm" color="gray.600">Immediate property value increase</Text>
          </Box>
          <Box textAlign="center">
            <Icon as={FaBolt} boxSize={12} color={SAND} mb={4} />
            <Heading size="md" mb={2}>+$45/wk</Heading>
            <Text fontSize="sm" color="gray.600">Higher rental income</Text>
          </Box>
          <Box textAlign="center">
            <Icon as={FaSolarPanel} boxSize={12} color={SAND} mb={4} />
            <Heading size="md" mb={2}>-$2,200/yr</Heading>
            <Text fontSize="sm" color="gray.600">Energy cost savings</Text>
          </Box>
          <Box textAlign="center">
            <Icon as={FaCheckCircle} boxSize={12} color={TEAL} mb={4} />
            <Heading size="md" mb={2}>3-4 Years</Heading>
            <Text fontSize="sm" color="gray.600">ROI payback period</Text>
          </Box>
        </SimpleGrid>
      </Container>

      <Box bg={TEAL} color="white" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <HStack><Icon as={FaHome} boxSize={10} /><Text fontSize="3xl">+</Text><Icon as={FaSolarPanel} boxSize={10} /></HStack>
            <Heading size="xl">Ready to Maximize Your Investment?</Heading>
            <Text fontSize="lg">Book a consultation to discuss the Property + Solar Package</Text>
            <Button size="lg" bg="white" color={TEAL} _hover={{ bg: "gray.100" }}>Get Started</Button>
          </VStack>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default PropertyServicesPropertySolarPackage;
